import {
  animate,
  animateChild,
  group,
  query,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Host,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  Inject,
} from '@angular/core';
import {
  asyncScheduler,
  BehaviorSubject,
  EMPTY,
  fromEvent,
  merge,
  Subject,
  subscribeOn,
  Subscription,
  switchMap,
  takeUntil,
} from 'rxjs';
import {
  TreeItemLike,
  TreeListComponent,
} from '../tree-list/tree-list.component';

const timingAnim = animate('300ms ease-in-out');

@Component({
  selector: 'app-side-menu',
  templateUrl: 'side-menu.component.html',
  styleUrls: ['side-menu.component.scss'],
  animations: [
    trigger('handleOpened', [
      state(
        'false',
        style({
          transform: 'rotate(180deg)',
        })
      ),
      state(
        'true',
        style({
          transform: 'rotate(0deg)',
        })
      ),
      transition('true <=> false', timingAnim),
    ]),
    trigger('logoOpened', [
      state(
        'false',
        style({
          opacity: 0,
        })
      ),
      state(
        'true',
        style({
          opacity: 1,
        })
      ),
      transition('true <=> false', timingAnim),
    ]),
    trigger('menuOpened', [
      state(
        'false',
        style({
          width: 'var(--sidemenu-width)',
        })
      ),
      state(
        'true',
        style({
          width: 'var(--sidemenu-opened-width)',
        })
      ),
      transition(
        'true <=> false',
        group([
          query('[childItemsToAnim]', animateChild(), { optional: true }),
          timingAnim,
        ])
      ),
    ]),
  ],
  host: {
    '[@menuOpened]': '!!openedState',
    '(@menuOpened.start)': 'onTriggerEventStart($event)',
    '(@menuOpened.done)': 'onTriggerEventDone($event)',
  },
})
export class SideMenuComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('menuItemsContentEl', { static: true })
  menuItemsContentElRef: ElementRef<HTMLDivElement>;

  @ViewChild(TreeListComponent, { static: true })
  treelistElRef: TreeListComponent;

  private _openedInternalSubject = new BehaviorSubject<boolean>(false);
  @Input()
  get opened() {
    return this._openedInternalSubject.getValue();
  }

  set opened(value) {
    this._openedInternalSubject.next(!!value);
  }

  @Output()
  openedChange = new EventEmitter<boolean>();

  @Output()
  onOpening = new EventEmitter<any>(true);
  @Output()
  onOpened = new EventEmitter<any>(true);
  @Output()
  onClosing = new EventEmitter<any>(true);
  @Output()
  onClosed = new EventEmitter<any>(true);

  @Output()
  onLogoClick = new EventEmitter<any>();

  @HostBinding('[class.has-toggle-button]')
  @Input()
  showToggleButton = true;

  @Input()
  showLogo = true;

  @Input()
  textEnabled: boolean;

  get isTextShown() {
    return !!(this.textEnabled ?? this.openedInternal);
  }

  @Input()
  openTriggerMode: 'menu-item-click' | 'mouseover' | 'none' = 'none';
  @Input()
  closeTriggerMode: 'host-click' | 'mouseleave' | 'none' = 'none';

  @Input()
  closeOutsideClickEnabled = false;

  initialized = false;

  openedState = false;

  openedInternal = false;
  opening = false;
  closing = false;
  closed = true;

  @Input()
  menuItems: TreeItemLike[] = [];

  private closeOutsideClickSubscription: Subscription;

  private lifecycle = new Subject<void>();

  constructor(
    @Host()
    private elementRef: ElementRef<HTMLElement>,
    @Inject(DOCUMENT) private _document: Document,
    private renderer: Renderer2,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.openedInternal = this.opened;
    this.closed = !this.opened;

    this.initialized = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.opened) {
      this.openedState = this.opened;
    }

    if (!this.initialized || changes.closeOutsideClickEnabled) {
      this.handleCloseOutsideClick();
    }
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();

    this.menuItems = null;
  }

  onTriggerEventStart(e) {
    if (!this.initialized) {
      return;
    }

    this.opening = e.fromState === false && e.toState === true;
    this.closing = !this.opening;

    this.openedInternal = false;
    this.closed = false;

    this.syncHostStateClass();
    this.changeDetector.detectChanges();

    if (this.opening) {
      this.onOpening.emit();
    } else if (this.closing) {
      this.onClosing.emit();
    }
  }

  onTriggerEventDone(e) {
    if (!this.opening && !this.closing) {
      return;
    }

    if (
      (this.opening && !this.openedState) ||
      (this.closing && this.openedState)
    ) {
      return;
    }

    this.openedInternal = this.opening;

    const shouldEmitOpenedChanges = this.openedInternal !== this.opened;

    this.opened = this.openedInternal;
    this.closed = !this.openedInternal;

    if (this.opening) {
      this.opening = false;
      this.syncHostStateClass();
      this.changeDetector.detectChanges();

      if (shouldEmitOpenedChanges) {
        this.openedChange.emit(this.opened);
      }

      this.onOpened.emit();
    } else if (this.closing) {
      this.closing = false;
      this.syncHostStateClass();
      this.changeDetector.detectChanges();

      if (shouldEmitOpenedChanges) {
        this.openedChange.emit(this.opened);
      }

      this.onClosed.emit();
      this.treelistElRef.closeItems();
    }
  }

  @HostListener('mouseover', ['$event'])
  onHostMouseOver(e: MouseEvent) {
    if (this.openTriggerMode === 'mouseover') {
      this.doOpen();
    }
  }

  @HostListener('mouseleave', ['$event'])
  onHostMouseLeave(e: MouseEvent) {
    if (this.closeTriggerMode === 'mouseleave') {
      this.doClose();
    }
  }

  @HostListener('click', ['$event'])
  onHostClick(e) {
    if (
      this.closeTriggerMode === 'host-click' &&
      !this.menuItemsContentElRef?.nativeElement?.contains(e.target)
    ) {
      this.doClose();
    }
  }

  onBtnToggleClick(e) {
    this.openedState = !this.openedState;
  }

  onLogoClickInternal(e) {
    this.onLogoClick.emit(e);
  }

  onTreeItemClick(e) {
    const item: TreeItemLike = e.itemData;
    if (!item.items?.length) {
      e.preventDefault();
      item.selected = !item.link;
    }

    if (this.openTriggerMode === 'menu-item-click') {
      this.doOpen();
    }
  }

  private doOpen() {
    this.openedState = true;
  }

  private doClose() {
    this.openedState = false;
  }

  private handleCloseOutsideClick() {
    this.closeOutsideClickSubscription?.unsubscribe();
    if (!this.closeOutsideClickEnabled) {
      return;
    }

    const element = this._document.body;
    this.closeOutsideClickSubscription = this._openedInternalSubject
      .pipe(
        switchMap((opened) => {
          if (!opened) {
            return EMPTY;
          }

          return merge(
            fromEvent(element, 'click', {
              passive: true,
            }),
            fromEvent(element, 'touchstart', {
              passive: true,
            })
          ).pipe(subscribeOn(asyncScheduler));
        })
      )
      .pipe(takeUntil(this.lifecycle))
      .subscribe((e) => {
        if (
          !this.opened ||
          this.elementRef?.nativeElement?.contains(e.target as any)
        ) {
          return;
        }

        this.doClose();
      });
  }

  private syncHostStateClass() {
    const aux: [boolean, string][] = [
      [this.openedInternal, 'state-opened'],
      [this.opening, 'state-opening'],
      [this.closing, 'state-closing'],
      [this.closed, 'state-closed'],
    ];

    aux.forEach(([value, className]) => {
      if (value) {
        this.renderer.addClass(this.elementRef.nativeElement, className);
      } else {
        this.renderer.removeClass(this.elementRef.nativeElement, className);
      }
    });
  }
}
